import { AlertCircle, ArrowLeft, ArrowRight, Lock } from "lucide-react";
import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Flag from "react-world-flags";

import type { AppDispatch } from "@/app/store";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { PayoutsVerificationGate } from "@/modules/verification/components/PayoutsVerificationGate";
import { useOrganizationVerificationStatus } from "@/modules/verification/hooks/useOrganizationVerificationStatus";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { ColocaStepper } from "@/shared/components/navigation/ColocaStepper";
import { useQuoteExpiry } from "@/shared/hooks/useQuoteExpiry";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { railLabelForCountry } from "@/shared/utils/railDisplay";

import { DispersionResultScreens } from "../components/DispersionForm/DispersionResultScreens";
import { QuoteRateBar } from "../components/DispersionForm/QuoteRateBar";
import { Step1AmountConversionPage } from "../components/DispersionForm/Step1AmountConversionPage";
import { Step2CountryFields } from "../components/DispersionForm/Step2CountryFields";
import { Step3Confirmation } from "../components/DispersionForm/Step3Confirmation";
import { QuoteExpiredModal } from "../components/QuoteExpiredModal";
import { COUNTRIES } from "../config/countries.config";
import { railForQuote, resolveRuntimeCorridor } from "../constants/dispersions.constants";
import { useDispersionFormErrors } from "../hooks/useDispersionFormErrors";
import { useDispersions } from "../hooks/useDispersions";
import { useSubmitDispersion } from "../hooks/useSubmitDispersion";
import { setFormSubmitError, setQuoteError } from "../store/dispersionsSlice";
import type { CountryCode } from "../types/countryFields.types";
import type { DispersionFormData, DispersionQuoteParams } from "../types/dispersions-api.types";
import { resolveDestinationParam, resolveOriginCurrencyParam } from "../utils/currencyDisplay";
import { parseAmountInputDecimal } from "../utils/currencyFormatter";

const ORIGIN_PARAM = "origin_currency";
const LEGACY_ORIGIN_PARAM = "origin";
const ORIGIN_LOCKED_PARAM = "origin_locked";
const DESTINATION_PARAM = "destination";

export const DispersionsListPage = () => {
  const { t: tGlobal } = useTranslations("global");
  const { t } = useTranslations("dispersions");
  const { user } = useAuth();
  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_billeteras") },
      { label: tGlobal("sidebar_menu_payouts"), to: "/app/dispersions" },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const {
    countries,
    countriesLoading,
    countriesError,
    runtimeWalletsByCurrency,
    isWalletListSettled,
    quoteLoading,
    quoteData,
    quoteError,
    currentStep,
    formData,
    isSubmitting,
    submitError,
    lastChangedAmountField,
    fetchQuote,
    goToStep,
    resetDispersionForm,
    restartDispersionForm,
    updateField,
    markQuoteExpired,
  } = useDispersions();

  // The organization's own verification decides whether money may leave at all. Rendered as a gate below,
  // after every hook; INTERNAL staff are never gated client-side (the backend 403 covers them).
  const verification = useOrganizationVerificationStatus();

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isQuoteExpiredModalOpen, setIsQuoteExpiredModalOpen] = useState(false);
  const [isRecalculatingQuote, setIsRecalculatingQuote] = useState(false);
  const [isOriginLocked, setIsOriginLocked] = useState(false);

  // Ref that stores the origin coming from the dashboard
  const lockedOriginRef = useRef<string | null>(null);
  // An origin the URL names but does not lock (a refresh of a hand-picked one), applied once.
  const pendingOriginRef = useRef<string | null>(null);
  // The destination a deep link asked for (the COP send panel, when another country is picked).
  const pendingDestinationRef = useRef<string | null>(null);
  // Ref to avoid multiple initializations
  const hasProcessedInitialParam = useRef(false);

  // Submission orchestration (per-corridor payload, idempotency key, result phases) lives in the hook —
  // the page only wires its origin-lock cleanup and renders by phase.
  const handleSuccessCleanup = useCallback(() => {
    setIsOriginLocked(false);
    lockedOriginRef.current = null;
  }, []);
  // Declared before the submit hook, which takes it as `onQuoteExpired`.
  const handleQuoteExpired = useCallback(() => {
    // Both: the modal is the prompt, the flag is what the confirm button can actually read. The modal alone
    // fires once per `expires_at`, so once dismissed nothing else remembered the quote was dead.
    markQuoteExpired();
    // The modal is a fresh context, and its alert is scoped to what happens INSIDE it. `quote.error` is only
    // cleared by a successful quote, so without this the modal could open announcing an unrelated failure
    // from an earlier request.
    dispatch(setQuoteError(null));
    setIsQuoteExpiredModalOpen(true);
  }, [markQuoteExpired, dispatch]);

  const {
    resultPhase,
    payoutStatus,
    setResultPhase,
    showResultDetails,
    setShowResultDetails,
    successModalData,
    setSuccessModalData,
    handleFinalSubmit,
    resetIdempotencyKey,
    errorKind,
  } = useSubmitDispersion({
    onSuccessCleanup: handleSuccessCleanup,
    // The re-quote modal is page state, so the submit hook hands the decision back
    // instead of failing: an expired price needs a new quote, not an error screen.
    onQuoteExpired: handleQuoteExpired,
  });

  // The origin and the destination live in the URL, so a refresh or a shared link lands on the same send.
  // `origin_currency` alone locks the origin, as the wallet cards and the COP send panel link it;
  // `origin_locked=0` marks one the user picked, which a refresh restores without locking. `origin` (an
  // ISO3 country) is still read for the wallet card's old links.
  const originParam = searchParams.get(ORIGIN_PARAM) ?? searchParams.get(LEGACY_ORIGIN_PARAM);
  const destinationParam = searchParams.get(DESTINATION_PARAM);
  const originLockedParam = searchParams.get(ORIGIN_LOCKED_PARAM);

  // Read once, on arrival. Later changes to the URL are this page mirroring its own form.
  useEffect(() => {
    if (hasProcessedInitialParam.current) return;
    hasProcessedInitialParam.current = true;
    resetDispersionForm();
    pendingDestinationRef.current = destinationParam;
    const locked = !!originParam && originLockedParam !== "0";
    lockedOriginRef.current = locked ? originParam : null;
    pendingOriginRef.current = locked ? null : originParam;
    setIsOriginLocked(locked);
  }, [originParam, destinationParam, originLockedParam, resetDispersionForm]);

  // Preselects the origin once the countries AND the wallet list are in: deciding before the list arrives
  // read every currency as "no wallet" and dropped the origin.
  useEffect(() => {
    const lockedOrigin = lockedOriginRef.current ?? pendingOriginRef.current;

    if (lockedOrigin && countries.length > 0 && isWalletListSettled && !formData.origin_currency) {
      pendingOriginRef.current = null;
      const currency = resolveOriginCurrencyParam(lockedOrigin, countries);
      // Only lock a wallet the org actually holds: the param can name a currency
      // with no wallet, and pre-selecting it would surface "you have no wallet in
      // X" out of nowhere. Fall back to a normal, unlocked entry instead.
      if (currency && runtimeWalletsByCurrency[currency]) {
        updateField("origin_currency", currency);
      } else {
        setIsOriginLocked(false);
        lockedOriginRef.current = null;
      }
    }
  }, [countries, formData.origin_currency, runtimeWalletsByCurrency, isWalletListSettled, updateField]);

  useEffect(() => {
    const pending = pendingDestinationRef.current;
    if (!pending || countries.length === 0 || formData.destination) return;
    pendingDestinationRef.current = null;
    const destination = resolveDestinationParam(pending, countries);
    if (destination) updateField("destination", destination);
  }, [countries, formData.destination, updateField]);

  // Mirrors the form into the URL (replace, so it adds no history), once what the URL asked for is applied.
  useEffect(() => {
    if (!hasProcessedInitialParam.current || pendingOriginRef.current || pendingDestinationRef.current)
      return;
    if (lockedOriginRef.current && !formData.origin_currency) return;
    const next = new URLSearchParams(searchParams);
    next.delete(LEGACY_ORIGIN_PARAM);
    if (formData.origin_currency) next.set(ORIGIN_PARAM, formData.origin_currency);
    else next.delete(ORIGIN_PARAM);
    if (formData.origin_currency && !isOriginLocked) next.set(ORIGIN_LOCKED_PARAM, "0");
    else next.delete(ORIGIN_LOCKED_PARAM);
    if (formData.destination) next.set(DESTINATION_PARAM, formData.destination);
    else next.delete(DESTINATION_PARAM);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [formData.origin_currency, formData.destination, isOriginLocked, searchParams, setSearchParams]);

  // All step validation (Yup + limits + runtime/PHP funds + KYC gate) lives in the hook — the page only
  // consumes the memoized result to gate the footer button and feed the steps.
  const { formErrors, isFormValid, quoteBlockReason, recipientBlockReason } = useDispersionFormErrors();

  // Stable identity so the memoized steps don't re-render when unrelated page state changes.
  const handleBlur = useCallback((field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  // Expiry EVENT only — no per-second ticking at page level. The visual countdowns live inside
  // `QuoteRateBar` (steps 2/3) and `QuoteSummaryCard` (step 1), each with its own confined ticker.
  useQuoteExpiry(quoteData?.expires_at ?? null, handleQuoteExpired);

  // Recalculate quote with same parameters
  const handleRecalculateQuote = useCallback(async () => {
    setIsRecalculatingQuote(true);
    // Whatever the modal shows next belongs to THIS attempt.
    dispatch(setQuoteError(null));

    try {
      const corridor = resolveRuntimeCorridor(formData.destination);
      // Nothing to re-quote without a corridor or a parseable amount. The previous
      // version sent `0` when the parse failed, which the engine rejects.
      const raw =
        lastChangedAmountField === "destination" ? formData.amount_destination : formData.amount_origin;
      const amount = parseAmountInputDecimal(raw);
      if (!corridor || !formData.origin_currency || amount === null) {
        setIsQuoteExpiredModalOpen(false);
        goToStep(1);
        return;
      }

      const params: DispersionQuoteParams = {
        originCurrency: formData.origin_currency,
        destinationCountry: corridor,
        rail: railForQuote(corridor, formData.recipientFields),
      };
      if (lastChangedAmountField === "destination") params.receiveAmount = amount;
      else params.sendAmount = amount;

      // Only close on a quote that actually landed. The thunk resolves either way — a failed quote is state,
      // not an exception — so awaiting alone closed the modal over the expired price, and `useQuoteExpiry`
      // fires once per `expires_at`, so nothing brought it back. The banner keeps the error and the modal
      // keeps the way out.
      if (!(await fetchQuote(params))) return;

      // A fresh quote is a fresh operation: reusing the key would let the backend
      // replay the previous payout instead of pricing this one.
      resetIdempotencyKey();
      setIsQuoteExpiredModalOpen(false);
    } catch {
      // Error is handled by the thunk
    } finally {
      setIsRecalculatingQuote(false);
    }
  }, [formData, lastChangedAmountField, fetchQuote, goToStep, resetIdempotencyKey]);

  // Start over - go back to step 1 and reset form
  const handleStartOver = useCallback(() => {
    setIsQuoteExpiredModalOpen(false);
    // Fully reset the form (this also clears the quote)
    resetDispersionForm();
    resetIdempotencyKey();
    // Clear the touched-fields state to reset validations
    setTouched({});
    // If there is no origin locked from the dashboard, also clear the locked state
    if (!lockedOriginRef.current) {
      setIsOriginLocked(false);
    }
  }, [resetDispersionForm, resetIdempotencyKey]);

  const handleNext = () => {
    if (!isFormValid) {
      return;
    }

    if (currentStep < 3) {
      goToStep((currentStep + 1) as 1 | 2 | 3);
    } else if (currentStep === 3) {
      handleFinalSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      // The user is going to edit data: the next submit is a different operation.
      resetIdempotencyKey();
      dispatch(setFormSubmitError(null));
      goToStep((currentStep - 1) as 1 | 2 | 3);
      setTouched({});
    }
  };

  // Result-screen actions (the screens themselves live in DispersionResultScreens).
  const handleToggleResultDetails = useCallback(() => {
    setShowResultDetails((v) => !v);
  }, [setShowResultDetails]);

  const handleBackToWallets = useCallback(() => {
    navigate("/app/wallets");
  }, [navigate]);

  /**
   * Both receipt actions rebuild the wizard rather than resume it.
   *
   * The quote that was just redeemed is spent — single-use — so a repeat is a NEW operation: it needs a new
   * price and a new idempotency key, or the backend replays the payout that already went out. They land on
   * step 1 and walk the three steps again for the same reason: a repeat is still money leaving, and the price
   * it leaves at is not the one on the receipt.
   *
   * The seed is priced here instead of waiting for the user to touch an amount, because step 1 only quotes on
   * input — a seeded form would otherwise sit there with the amounts filled and no price, and the rate bar
   * empty. `pricedFrom` keeps the side the operator originally typed: re-pricing a fixed-receive send from
   * the origin amount would quietly change what the recipient gets.
   */
  const restartDispersion = useCallback(
    (seed: Partial<DispersionFormData>, pricedFrom: "origin" | "destination" | null) => {
      restartDispersionForm(seed);
      resetIdempotencyKey();
      setSuccessModalData(null);
      setResultPhase("idle");
      setShowResultDetails(false);
      handleSuccessCleanup();

      const corridor = resolveRuntimeCorridor(seed.destination ?? "");
      if (!seed.origin_currency || !corridor) return;
      const byReceive = pricedFrom === "destination";
      const amount = parseAmountInputDecimal(
        String((byReceive ? seed.amount_destination : seed.amount_origin) ?? ""),
      );
      if (amount === null) return;
      fetchQuote({
        originCurrency: seed.origin_currency,
        destinationCountry: corridor,
        rail: railForQuote(corridor, seed.recipientFields),
        ...(byReceive ? { receiveAmount: amount } : { sendAmount: amount }),
      });
    },
    [
      restartDispersionForm,
      resetIdempotencyKey,
      setSuccessModalData,
      setResultPhase,
      setShowResultDetails,
      handleSuccessCleanup,
      fetchQuote,
    ],
  );

  const handleSendAgain = useCallback(() => {
    if (!successModalData) return;
    const previous = successModalData.formData;
    restartDispersion(
      {
        ...previous,
        // A fresh object: the snapshot is React state, and seeding it directly would hand the live form the
        // very record the receipt is still rendering from.
        recipientFields: { ...previous.recipientFields },
      },
      successModalData.pricedFrom,
    );
  }, [successModalData, restartDispersion]);

  const handleSendToSomeoneElse = useCallback(() => {
    if (!successModalData) return;
    const previous = successModalData.formData;
    // Wallet, corridor and amount carry over; the recipient does not. Keeping the amount is the recoverable
    // choice of the two — a wrong amount is one field to correct, a cleared one is a number to retype — and
    // paying several people the same figure is why anyone presses this.
    restartDispersion(
      {
        origin_currency: previous.origin_currency,
        destination: previous.destination,
        amount_origin: previous.amount_origin,
        amount_destination: previous.amount_destination,
      },
      successModalData.pricedFrom,
    );
  }, [successModalData, restartDispersion]);

  const handleAdjustData = useCallback(() => {
    // The user will edit the data: new operation → new idempotency key. Editing the recipient or the concept
    // changes the body, and the backend hashes the body against the key — reusing it answers 409 in raw
    // English from the idempotency interceptor, which never goes through i18n.
    resetIdempotencyKey();
    // `submitError` is only cleared when a submit STARTS, so without this the previous failure keeps
    // rendering in the step alert as if it had just happened.
    dispatch(setFormSubmitError(null));
    setResultPhase("idle");
    goToStep(2);
  }, [resetIdempotencyKey, dispatch, setResultPhase, goToStep]);

  const handleRetrySubmit = useCallback(() => {
    dispatch(setFormSubmitError(null));
    setResultPhase("idle");
    goToStep(3);
  }, [dispatch, setResultPhase, goToStep]);

  if (
    countriesLoading ||
    (verification.isOrgMember && verification.isLoading && verification.data === null)
  ) {
    return (
      <div className="mx-auto w-full max-w-[720px] space-y-6 p-2">
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </div>
    );
  }

  if (countriesError) {
    return (
      <div className="mx-auto w-full max-w-[720px] space-y-6 p-2">
        <ColocaAlert variant="destructive" icon={AlertCircle} text={countriesError} />
      </div>
    );
  }

  // Same shape as the two gates above: the wizard is unreachable while payouts are blocked, so there is no
  // step-3 button to disable and no half-filled form to lose.
  if (verification.isOrgMember && !verification.canCreatePayouts) {
    return (
      <div className="mx-auto w-full max-w-[720px] space-y-6 p-2">
        <PayoutsVerificationGate status={verification} />
      </div>
    );
  }

  const destinationCountry = countries.find((c) => c.code === formData.destination);

  // Steps 2/3 resolve their country-specific fields by the CONFIG name key
  // ("México", "Brasil"…). The API name may differ ("Mexico"), so normalize by
  // ISO3 first and fall back to the API name.
  const toCanonicalName = (country: { code: string; name: string } | undefined): string | undefined =>
    country
      ? (Object.values(COUNTRIES).find((c) => c.iso3 === country.code)?.code ?? country.name)
      : undefined;
  const destinationCountryName = toCanonicalName(destinationCountry);

  const railLabel = destinationCountry
    ? railLabelForCountry(destinationCountry.code, t("rail_transfer"))
    : "";
  const corridorChip =
    formData.origin_currency && destinationCountry
      ? `${formData.origin_currency} → ${destinationCountry.currency}${railLabel ? ` · ${railLabel}` : ""}`
      : t("header_chip");
  const stepperSteps = [
    { label: t("stepper_amount") },
    { label: t("stepper_recipient") },
    { label: t("stepper_review") },
  ];
  // Countdown tick confined inside the component — the page renders it once and forgets.
  const rateBar = quoteData ? (
    <QuoteRateBar quote={quoteData} destinationCountry={destinationCountry} />
  ) : null;

  return (
    <div className="mx-auto w-full max-w-[720px] space-y-4 p-2">
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        {resultPhase !== "idle" && (
          <DispersionResultScreens
            phase={resultPhase}
            successData={successModalData}
            payoutStatus={payoutStatus}
            countries={countries}
            submitError={submitError}
            showDetails={showResultDetails}
            onToggleDetails={handleToggleResultDetails}
            onSendAgain={handleSendAgain}
            onSendToSomeoneElse={handleSendToSomeoneElse}
            organizationName={user?.organization?.name ?? null}
            onBackToWallets={handleBackToWallets}
            onAdjustData={handleAdjustData}
            onRetry={handleRetrySubmit}
            errorKind={errorKind}
          />
        )}

        {resultPhase === "idle" && (
          <>
            {/* Header + stepper */}
            <div className="flex flex-col gap-5 border-b border-border p-6">
              <div className="flex items-center gap-3.5">
                <div className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-xl border border-border bg-muted">
                  {destinationCountry ? (
                    <Flag
                      code={destinationCountry.flag}
                      className="h-[18px] w-[26px] rounded-[3px] object-cover"
                      aria-hidden
                    />
                  ) : (
                    <span aria-hidden="true">💸</span>
                  )}
                </div>
                <div className="min-w-0">
                  <h1 className="truncate text-lg font-semibold leading-tight tracking-tight text-foreground">
                    {t("header_title")}
                  </h1>
                  <p className="mt-0.5 truncate text-sm text-muted-foreground">{t("header_subtitle")}</p>
                </div>
                <div className="flex-1" />
                <ColocaBadge variant="default" className="shrink-0 whitespace-nowrap">
                  {corridorChip}
                </ColocaBadge>
              </div>

              {/* Zero-based: the wizard counts its steps from 1. */}
              <ColocaStepper
                steps={stepperSteps}
                currentStep={currentStep - 1}
                aria-label={t("header_title")}
              />
            </div>

            {/* Step body */}
            {currentStep === 1 && (
              <div className="p-6">
                <Step1AmountConversionPage
                  countries={countries}
                  errors={formErrors}
                  touched={touched}
                  onBlur={handleBlur}
                  isOriginLocked={isOriginLocked}
                />
              </div>
            )}

            {currentStep === 2 && destinationCountry && (
              <div className="flex flex-col gap-5 p-6">
                {rateBar}
                {formErrors.amount_limit && (
                  <ColocaAlert
                    variant="destructive"
                    icon={AlertCircle}
                    text={formErrors.amount_limit}
                    actions={
                      <ColocaButton size="sm" variant="default" onClick={() => goToStep(1)}>
                        {t("step2_limit_back")}
                      </ColocaButton>
                    }
                  />
                )}
                <Step2CountryFields
                  destination={(destinationCountryName ?? destinationCountry.name) as CountryCode}
                  destinationCurrency={destinationCountry.currency}
                  errors={formErrors}
                  touched={touched}
                  onBlur={handleBlur}
                />
              </div>
            )}

            {currentStep === 3 && formData.origin_currency && destinationCountry && (
              <div className="flex flex-col gap-4 p-6">
                {rateBar}
                {/*
                  Says WHY Authorize is off. Without it the guard would be a regression: the enabled button
                  used to be the only thing that reopened the re-quote modal (the submit guard called
                  `onQuoteExpired`), and `useQuoteExpiry` fires once per `expires_at`, so a dismissed modal
                  never came back on its own.

                  `role="status"` overrides the primitive's hardcoded `role="alert"` — this is a standing
                  state, not an event, and assertive would interrupt the screen reader on every step change.
                */}
                {quoteBlockReason && (
                  <ColocaAlert
                    role="status"
                    variant="warning"
                    icon={AlertCircle}
                    text={quoteBlockReason}
                    actions={
                      <ColocaButton
                        variant="default"
                        size="sm"
                        onClick={handleRecalculateQuote}
                        loading={isRecalculatingQuote}
                      >
                        {t("quote_expired_recalculate")}
                      </ColocaButton>
                    }
                  />
                )}
                {/*
                  Why Authorize is off for a reason that is not the price. Its own alert rather than a second
                  message in the one above, because the way out is different: a recipient the directory
                  refuses is fixed by going back to the key, never by re-quoting.
                */}
                {recipientBlockReason && (
                  <ColocaAlert
                    role="status"
                    variant="warning"
                    icon={AlertCircle}
                    text={recipientBlockReason}
                    actions={
                      <ColocaButton variant="default" size="sm" onClick={handlePrevious}>
                        {t("button_previous")}
                      </ColocaButton>
                    }
                  />
                )}
                <Step3Confirmation
                  formData={formData}
                  originCurrency={formData.origin_currency}
                  destinationCountry={(destinationCountryName ?? destinationCountry.name) as CountryCode}
                  quoteData={quoteData || undefined}
                />
              </div>
            )}

            {submitError && (
              <div className="px-6 pb-4">
                <ColocaAlert variant="destructive" icon={AlertCircle} text={submitError} />
              </div>
            )}

            {/* Footer */}
            <div
              className={cn(
                "flex border-t border-border px-6 py-4",
                currentStep > 1 ? "justify-between" : "justify-end",
              )}
            >
              {currentStep > 1 && (
                <ColocaButton
                  variant="ghost"
                  size="lg"
                  icon={ArrowLeft}
                  className="px-4"
                  onClick={handlePrevious}
                >
                  {t("button_previous")}
                </ColocaButton>
              )}
              <ColocaButton
                variant="primary"
                size="lg"
                icon={currentStep === 3 ? Lock : undefined}
                iconTrailing={currentStep === 3 ? undefined : ArrowRight}
                className="px-4"
                onClick={handleNext}
                loading={currentStep === 3 && isSubmitting}
                disabled={
                  !isFormValid || (currentStep === 1 && quoteLoading) || (currentStep === 3 && isSubmitting)
                }
              >
                {currentStep === 3 ? t("button_authorize") : t("button_continue")}
              </ColocaButton>
            </div>
          </>
        )}
      </div>

      <p className="text-center text-xs text-muted-foreground">{t("footer_rate_note")}</p>

      <QuoteExpiredModal
        isOpen={isQuoteExpiredModalOpen}
        onRecalculate={handleRecalculateQuote}
        onStartOver={handleStartOver}
        isRecalculating={isRecalculatingQuote}
        recalculateError={quoteError}
      />
    </div>
  );
};
